import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  clearStoredPlatformAdminKey,
  fetchPlatformOrgs,
  fetchPlatformSummary,
  fetchPlatformUsers,
  getStoredPlatformAdminKey,
  setStoredPlatformAdminKey,
  type PlatformOrgRow,
  type PlatformSummary,
  type PlatformUserRow,
} from "../../api/platform-admin";
import { ApiClientError } from "../../api/types";
import { isOpsPortalHost, opsPortalPath } from "../../lib/ops-host";

type Tab = "summary" | "orgs" | "users";

function subLabel(row: PlatformOrgRow["subscription"]) {
  if (row.isActive && row.isTrial) return `تجربة · ${row.daysRemaining} يوم`;
  if (row.isActive && row.paidUntil) return `مدفوع · ${row.daysRemaining} يوم`;
  if (row.isActive) return "نشط";
  return "منتهٍ";
}

export function PlatformAdminPage() {
  const [adminKey, setAdminKey] = useState(() => getStoredPlatformAdminKey() ?? "");
  const [unlocked, setUnlocked] = useState(() => Boolean(getStoredPlatformAdminKey()));
  const [tab, setTab] = useState<Tab>("summary");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState<PlatformSummary | null>(null);
  const [orgs, setOrgs] = useState<PlatformOrgRow[]>([]);
  const [users, setUsers] = useState<PlatformUserRow[]>([]);
  const [emailFilter, setEmailFilter] = useState("");

  const load = useCallback(async () => {
    const key = getStoredPlatformAdminKey();
    if (!key) return;
    setLoading(true);
    setError(null);
    try {
      if (tab === "summary") {
        setSummary(await fetchPlatformSummary(key));
      } else if (tab === "orgs") {
        const res = await fetchPlatformOrgs(key);
        setOrgs(res.items);
      } else {
        const res = await fetchPlatformUsers(key, 50, 0, emailFilter);
        setUsers(res.items);
      }
    } catch (err) {
      if (err instanceof ApiClientError && (err.status === 401 || err.status === 503)) {
        clearStoredPlatformAdminKey();
        setUnlocked(false);
      }
      setError(err instanceof Error ? err.message : "فشل التحميل");
    } finally {
      setLoading(false);
    }
  }, [tab, emailFilter]);

  useEffect(() => {
    if (unlocked) void load();
  }, [unlocked, load]);

  function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    const k = adminKey.trim();
    if (!k) return;
    setStoredPlatformAdminKey(k);
    setUnlocked(true);
    setError(null);
  }

  function handleLock() {
    clearStoredPlatformAdminKey();
    setUnlocked(false);
    setSummary(null);
    setOrgs([]);
    setUsers([]);
  }

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              إدارة المنصة {isOpsPortalHost() ? "· usr.godo-erp.com" : ""}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              للمشغّل فقط — غير ظاهرة في القائمة. لا تشارك المفتاح.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to={opsPortalPath("sheet")} className="text-sm font-semibold text-godo-accent hover:underline">
              جدول Google Sheet →
            </Link>
            <a href="https://pro.godo-erp.com/login" className="text-sm text-godo-accent hover:underline">
              ← ERP (pro)
            </a>
          </div>
        </div>

        {!unlocked ? (
          <form onSubmit={handleUnlock} className="mx-auto max-w-md rounded-xl bg-white p-6 shadow">
            <label className="block text-sm font-medium text-slate-700">مفتاح PLATFORM_ADMIN_SECRET</label>
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              autoComplete="off"
            />
            <button
              type="submit"
              className="mt-4 w-full rounded-lg bg-godo-accent py-2.5 text-sm font-semibold text-white"
            >
              فتح اللوحة
            </button>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          </form>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {(
                [
                  ["summary", "ملخص"],
                  ["orgs", "المنظمات"],
                  ["users", "المستخدمون"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium ${
                    tab === id ? "bg-godo-accent text-white" : "bg-white text-slate-700 shadow-sm"
                  }`}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => void load()}
                className="rounded-lg bg-white px-4 py-2 text-sm shadow-sm"
                disabled={loading}
              >
                تحديث
              </button>
              <button
                type="button"
                onClick={handleLock}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
              >
                قفل
              </button>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {loading && <p className="text-sm text-slate-500">جاري التحميل…</p>}

            {tab === "summary" && summary && !loading && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {(
                  [
                    ["users", "مستخدمون", summary.users],
                    ["orgs", "منظمات", summary.orgs],
                    ["subs", "اشتراكات", summary.subscriptions],
                    ["bl", "قائمة تجارب", summary.trialBlacklist],
                  ] as const
                ).map(([k, label, n]) => (
                  <div key={k} className="rounded-xl bg-white p-5 shadow">
                    <p className="text-sm text-slate-500">{label}</p>
                    <p className="mt-1 text-3xl font-bold text-slate-900">{n}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "orgs" && !loading && (
              <div className="overflow-x-auto rounded-xl bg-white shadow">
                <table className="min-w-full text-sm">
                  <thead className="border-b bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-right">الشركة</th>
                      <th className="px-3 py-2 text-right">الأونر</th>
                      <th className="px-3 py-2 text-right">مستخدمون</th>
                      <th className="px-3 py-2 text-right">الاشتراك</th>
                      <th className="px-3 py-2 text-right">org_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((o) => (
                      <tr key={o.orgId} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-medium">{o.legalName}</div>
                          <div className="text-xs text-slate-500">{o.orgCode}</div>
                        </td>
                        <td className="px-3 py-2" dir="ltr">
                          {o.ownerEmail ?? "—"}
                        </td>
                        <td className="px-3 py-2">{o.userCount}</td>
                        <td className="px-3 py-2">{subLabel(o.subscription)}</td>
                        <td className="px-3 py-2 font-mono text-xs" dir="ltr">
                          {o.orgId}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "users" && !loading && (
              <>
                <div className="mb-3 flex flex-wrap gap-2">
                  <input
                    type="search"
                    placeholder="بحث بريد…"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    dir="ltr"
                  />
                  <button
                    type="button"
                    onClick={() => void load()}
                    className="rounded-lg bg-godo-accent px-4 py-2 text-sm text-white"
                  >
                    بحث
                  </button>
                </div>
                <div className="overflow-x-auto rounded-xl bg-white shadow">
                  <table className="min-w-full text-sm">
                    <thead className="border-b bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-right">البريد</th>
                        <th className="px-3 py-2 text-right">الاسم</th>
                        <th className="px-3 py-2 text-right">الشركة</th>
                        <th className="px-3 py-2 text-right">أونر</th>
                        <th className="px-3 py-2 text-right">آخر دخول</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.userId} className="border-b border-slate-100">
                          <td className="px-3 py-2" dir="ltr">
                            {u.email}
                          </td>
                          <td className="px-3 py-2">{u.displayName}</td>
                          <td className="px-3 py-2">{u.orgName}</td>
                          <td className="px-3 py-2">{u.ownerFlag ? "نعم" : "—"}</td>
                          <td className="px-3 py-2" dir="ltr">
                            {u.lastLoginAt ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <p className="mt-6 text-xs text-slate-500">
              لتمديد اشتراك:{" "}
              <code dir="ltr" className="rounded bg-slate-200 px-1">
                npm run ops:extend-subscription -- --email USER --trial-days 90
              </code>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
